import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { initSocket, disconnectSocket, onUnreadCountUpdate, onAdminNotification } from '../services/socket';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);

  // Initialiser le socket
  const connectSocket = useCallback((token) => {
    const socket = initSocket(token);

    if (socket) {
      socket.on('connect', () => {
        setSocketConnected(true);
      });

      socket.on('disconnect', () => {
        setSocketConnected(false);
      });

      // Écouter les mises à jour du compteur de messages non lus
      onUnreadCountUpdate((data) => {
        setUnreadMessages(data.count);
      });

      // Écouter les nouvelles notifications admin
      onAdminNotification(() => {
        setUnreadNotifications(prev => prev + 1);
      });
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');

      if (token && userData) {
        // Configurer axios avec le token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        try {
          // Valider le token en appelant le profil
          const res = await axios.get(`${API_URL}/auth/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Token valide : mettre à jour l'utilisateur avec les données fraîches
          const freshUser = {
            id: res.data.id,
            username: res.data.username,
            email: res.data.email,
            role: res.data.role,
            registrationPaid: res.data.registrationPaid
          };
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));

          // Initialiser le socket et les notifications
          connectSocket(token);
          axios.get(`${API_URL}/notifications/unread-count`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => setUnreadNotifications(r.data.count)).catch(() => {});

        } catch (err) {
          // Token expiré ou invalide : déconnecter proprement
          console.log('Token expiré, déconnexion automatique');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
      }

      setLoading(false);
    };

    initAuth();

    // Écouter l'événement de token expiré (émis par l'intercepteur api.js)
    const handleTokenExpired = () => {
      setUser(null);
      setUnreadMessages(0);
      setUnreadNotifications(0);
      disconnectSocket();
    };
    window.addEventListener('auth:token-expired', handleTokenExpired);

    // Cleanup au démontage
    return () => {
      disconnectSocket();
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, [connectSocket]);

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { token, user: newUser } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Connecter le socket après inscription
    connectSocket(token);
    return response.data;
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Connecter le socket après connexion
    connectSocket(token);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setUnreadMessages(0);
    setUnreadNotifications(0);
    delete axios.defaults.headers.common['Authorization'];
    // Déconnecter le socket
    disconnectSocket();
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    localStorage.setItem('user', JSON.stringify(newUserData));
    setUser(newUserData);
  };

  const value = {
    user,
    register,
    login,
    logout,
    updateUser,
    loading,
    unreadMessages,
    setUnreadMessages,
    unreadNotifications,
    setUnreadNotifications,
    socketConnected
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
